/**
 * Retrieval Details Modal Component
 * 
 * This component provides a modal interface for viewing retrieval request details
 * with comprehensive information display.
 */

import React from 'react';
import { useTranslations } from '../../context/TranslationsContext';
import { useApp } from '../../context/AppContext';

const RetrievalDetailsModal = ({ request, onClose }) => {
  const { translations } = useTranslations();
  const { state } = useApp();
  const t = translations[state.currentLanguage];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US').format(price);
  };

  const createGoogleMapsLink = (address) => {
    if (!address) return null;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      retrieval_requested: 'bg-yellow-100 text-yellow-800',
      retrieval_approved: 'bg-blue-100 text-blue-800',
      retrieval_delivering: 'bg-purple-100 text-purple-800',
      retrieval_completed: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (!request) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>تفاصيل طلب الاسترجاع</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="modal-body">
          <div className="order-info">
            <h4>طلب #{request.short_id || request.id?.substring(0, 8)}</h4>
            <p><strong>{t.status}:</strong> 
              <span className={`status-badge ${getStatusColor(request.status)}`}>
                {t[request.status]}
              </span>
            </p>
          </div>

          <div className="details-grid">
            <div className="detail-item">
              <label>{t.customerName}:</label>
              <span>{request.customer_name}</span>
            </div>
            
            <div className="detail-item">
              <label>{t.phone}:</label>
              <span>{request.customer_phone}</span>
            </div>
            
            <div className="detail-item">
              <label>عنوان التوصيل:</label>
              <span>
                {request.retrieval_delivery_address ? (
                  <a 
                    href={createGoogleMapsLink(request.retrieval_delivery_address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="address-link"
                    style={{
                      color: '#1976d2',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    {request.retrieval_delivery_address} 📍
                  </a>
                ) : (
                  'غير محدد'
                )}
              </span>
            </div>
            
            <div className="detail-item">
              <label>تاريخ طلب الاسترجاع:</label>
              <span>{formatDate(request.retrieval_requested_at || request.created_at)}</span>
            </div>
            
            <div className="detail-item">
              <label>تاريخ التوصيل المطلوب:</label>
              <span>{request.retrieval_delivery_date ? formatDate(request.retrieval_delivery_date) : 'غير محدد'}</span>
            </div>
            
            <div className="detail-item">
              <label>وصف الأغراض:</label>
              <span>{request.items_description}</span>
            </div>
            
            <div className="detail-item">
              <label>{t.space}:</label>
              <span>{request.space_required} {t.sqm}</span>
            </div>
            
            <div className="detail-item">
              <label>{t.duration}:</label>
              <span>{request.duration_months} {t.months}</span>
            </div>
            
            <div className="detail-item">
              <label>{t.price}:</label>
              <span>{formatPrice(request.total_price)} {t.sar}</span>
            </div>
            
            {request.admin_notes && (
              <div className="detail-item">
                <label>ملاحظات الإدارة:</label>
                <span>{request.admin_notes}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
};

export default RetrievalDetailsModal;
